import { computed, getCurrentInstance, onMounted, ref } from 'vue';
import SalesTransactionsAPI from 'dashboard/api/salesTransactions';
import { deliveryFormFromTransaction } from './salesDeliveryForm';
import { responsibleMode } from './salesDeliveryValidation';
import { useSalesDeliveryFormState } from './useSalesDeliveryFormState';

const errorMessage = (error, fallback) =>
  error?.response?.data?.message || error?.response?.data?.error || fallback;

export function useSalesDelivery({ id, t, notify }) {
  const transaction = ref(null);
  const catalog = ref({ list_bancos: [] });
  const recipients = ref({ participants: [], copies: [] });
  const form = ref(deliveryFormFromTransaction(null));
  const errors = ref({});
  const loading = ref(false);
  const saving = ref(false);
  const delivering = ref(false);
  const recipientSaving = ref(false);

  const participants = computed(
    () => recipients.value.participants || transaction.value?.participants || []
  );
  const copies = computed(() => recipients.value.copies || []);
  const bankOptions = computed(() => catalog.value.list_bancos || []);
  const responsibleType = computed(() =>
    responsibleMode(form.value.forma_pagamento)
  );
  const canSave = computed(() =>
    Boolean(transaction.value?.permissions?.can_save_draft)
  );
  const isForbidden = computed(
    () => transaction.value?.permissions?.can_save_draft === false
  );
  const canDeliver = computed(() =>
    Boolean(transaction.value?.permissions?.can_deliver)
  );
  const canEdit = computed(() => canSave.value || canDeliver.value);
  const { update, updateWitness, validate } = useSalesDeliveryFormState({
    errors,
    form,
    participants,
    t,
  });

  const load = async () => {
    if (!id.value) return null;
    loading.value = true;
    try {
      const [transactionResponse, catalogResponse, recipientsResponse] =
        await Promise.all([
          SalesTransactionsAPI.show(id.value),
          SalesTransactionsAPI.deliveryCatalog(id.value),
          SalesTransactionsAPI.deliveryRecipients(id.value),
        ]);
      transaction.value = transactionResponse?.data || null;
      form.value = deliveryFormFromTransaction(transaction.value);
      catalog.value = catalogResponse?.data?.catalog || { list_bancos: [] };
      recipients.value = recipientsResponse?.data?.data || {
        participants: transaction.value?.participants || [],
        copies: transaction.value?.profile?.email_copies || [],
      };
      return transaction.value;
    } catch (error) {
      notify(errorMessage(error, t('SALES.DELIVERY.ERRORS.LOAD')));
      return null;
    } finally {
      loading.value = false;
    }
  };

  const saveRecipients = async payload => {
    recipientSaving.value = true;
    try {
      const response = await SalesTransactionsAPI.updateDeliveryRecipients(
        id.value,
        payload
      );
      recipients.value = response?.data?.data || recipients.value;
      notify(t('SALES.DELIVERY.ALERTS.RECIPIENTS_SAVED'));
      return recipients.value;
    } catch (error) {
      notify(errorMessage(error, t('SALES.DELIVERY.ERRORS.RECIPIENTS')));
      return null;
    } finally {
      recipientSaving.value = false;
    }
  };

  const save = async () => {
    if (!id.value) return null;
    saving.value = true;
    try {
      const response = await SalesTransactionsAPI.saveDraft(
        id.value,
        form.value
      );
      transaction.value = response?.data || transaction.value;
      notify(t('SALES.DELIVERY.ALERTS.SAVED'));
      return transaction.value;
    } catch (error) {
      notify(errorMessage(error, t('SALES.DELIVERY.ERRORS.SAVE')));
      return null;
    } finally {
      saving.value = false;
    }
  };

  const deliver = async () => {
    if (!validate()) {
      notify(t('SALES.DELIVERY.ERRORS.FORM'));
      return null;
    }
    delivering.value = true;
    try {
      const response = await SalesTransactionsAPI.deliver(id.value, form.value);
      transaction.value = response?.data || transaction.value;
      notify(t('SALES.DELIVERY.ALERTS.DELIVERED'));
      return transaction.value;
    } catch (error) {
      notify(errorMessage(error, t('SALES.DELIVERY.ERRORS.DELIVER')));
      return null;
    } finally {
      delivering.value = false;
    }
  };

  if (getCurrentInstance()) onMounted(load);

  return {
    bankOptions,
    canDeliver,
    canEdit,
    canSave,
    catalog,
    deliver,
    delivering,
    errors,
    form,
    isForbidden,
    load,
    loading,
    participants,
    copies,
    recipientSaving,
    saveRecipients,
    responsibleType,
    save,
    saving,
    transaction,
    update,
    updateWitness,
    validate,
  };
}
